import { Component, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import dayjs from 'dayjs';
import { BestyrelseService } from '@features/letpensiondk/letpensiondk-bestyrelse/bestyrelse.service';
import { Bestyrelsesmedlem } from '@features/letpensiondk/letpensiondk-bestyrelse/bestyrelsesmedlem.model';
import { BestyrelseDatasource } from '@features/letpensiondk/letpensiondk-bestyrelse/bestyrelse.datasource';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-letpensiondk-bestyrelse-detail',
  imports: [
    ReactiveFormsModule,
    InputText,
    ButtonModule,
    MessageModule,
    ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './letpensiondk-bestyrelse-detail.component.html',
  styleUrl: './letpensiondk-bestyrelse-detail.component.css'
})
export class LetpensiondkBestyrelseDetailComponent {
  confirmationService = inject(ConfirmationService);
  bestyrelseService = inject(BestyrelseService);
  bestyrelseDs = inject(BestyrelseDatasource);
  form: FormGroup;

  saving = signal(false);

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      navn: ['', { validators: [Validators.required] }],
      bestyrelsesTitel: [''],
      stilling: [''],
      raekkeFoelge: [''],
      oprettet: new FormControl<Date | undefined>(undefined, {
        validators: [Validators.required]
      })

    });

    effect(() => {
      const bestyrelsesmedlem = this.bestyrelseService.selectedBestyrelsesmedlem();
      if (!bestyrelsesmedlem) {
        this.form.reset();
        this.form.patchValue({
          oprettet: dayjs().toDate()
        });
      } else {
        this.form.setValue({
          navn: bestyrelsesmedlem.navn || '',
          bestyrelsesTitel: bestyrelsesmedlem.bestyrelsesTitel || '',
          stilling: bestyrelsesmedlem.stilling || '',
          raekkeFoelge: bestyrelsesmedlem.raekkeFoelge || '',
          oprettet: bestyrelsesmedlem.oprettet || ''
        });
      }
    });
  }

  async onClickSave() {
    await this.saveChanges();
  }

  async onClickDelete(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Er du sikker?',
      header: 'Slet bestyrelsesmedlem',
      rejectButtonProps: {
        label: 'Fortryd',
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: 'Slet',
        severity: 'danger'
      },

      accept: async () => {
        const id = this.bestyrelseService.selectedBestyrelsesmedlem()?.id;
        if (id) {
          await this.bestyrelseDs.remove(id);
        }
        this.bestyrelseService.activeTab.set('list');
      }
    });

  }

  async saveChanges() {
    if (this.form.invalid) {
      return;
    }

    this.saving.set(true);

    const bestyrelsesmedlem = this.bestyrelseService.selectedBestyrelsesmedlem();

    const newData = {
      ...bestyrelsesmedlem,
      ...this.form.value
    } as Bestyrelsesmedlem;

    if (bestyrelsesmedlem?.id) {
      await this.bestyrelseDs.update(bestyrelsesmedlem.id, newData);
    } else {
      await this.bestyrelseDs.create(newData);
    }

    this.saving.set(false);
    this.bestyrelseService.activeTab.set('list');
  }
}
